import { useMemo } from 'react';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface Criterion {
  label: string;
  met: boolean;
}

// Exported for use in validation schemas
export const PASSWORD_MIN_LENGTH = 8;

// Pattern for special characters
export const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;

export const PASSWORD_CRITERIA = {
  minLength: PASSWORD_MIN_LENGTH,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
};

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const criteria: Criterion[] = useMemo(() => [
    { label: `Au moins ${PASSWORD_MIN_LENGTH} caractères`, met: password.length >= PASSWORD_MIN_LENGTH },
    { label: 'Une lettre majuscule', met: /[A-Z]/.test(password) },
    { label: 'Une lettre minuscule', met: /[a-z]/.test(password) },
    { label: 'Un chiffre', met: /[0-9]/.test(password) },
    { label: 'Un caractère spécial (!@#$...)', met: SPECIAL_CHAR_REGEX.test(password) },
  ], [password]);

  const strength = useMemo(() => {
    const metCount = criteria.filter(c => c.met).length;
    if (metCount === 0) return { level: 0, label: '', color: 'bg-muted' };
    if (metCount <= 2) return { level: 1, label: 'Faible', color: 'bg-destructive' };
    if (metCount === 3) return { level: 2, label: 'Moyen', color: 'bg-orange-500' };
    if (metCount === 4) return { level: 3, label: 'Bon', color: 'bg-yellow-500' };
    return { level: 4, label: 'Excellent', color: 'bg-success' };
  }, [criteria]);

  if (!password) return null;

  return (
    <div className="space-y-3 mt-2">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex gap-1">
          {[1, 2, 3, 4].map((level) => (
            <div
              key={level}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                level <= strength.level ? strength.color : 'bg-muted'
              }`}
            />
          ))}
        </div>
        {strength.label && (
          <p className={`text-xs font-medium ${
            strength.level === 1 ? 'text-destructive' :
            strength.level === 2 ? 'text-orange-500' :
            strength.level === 3 ? 'text-yellow-600' :
            'text-green-600'
          }`}>
            {strength.label}
          </p>
        )}
      </div>

      {/* Criteria checklist */}
      <ul className="space-y-1">
        {criteria.map((criterion, index) => (
          <li
            key={index}
            className={`flex items-center gap-2 text-xs ${
              criterion.met ? 'text-green-600' : 'text-muted-foreground'
            }`}
          >
            {criterion.met ? (
              <Check className="h-3 w-3" />
            ) : (
              <X className="h-3 w-3" />
            )}
            {criterion.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Helper function to validate password against all criteria
export function isPasswordValid(password: string): boolean {
  return (
    password.length >= PASSWORD_MIN_LENGTH &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    SPECIAL_CHAR_REGEX.test(password)
  );
}
